import { useCallback, useMemo, useState } from "react"

const defaultFieldState = {
  value: "",
  inputValue: "",
  invalid: false,
  pendingResolution: false,
}

const normalizeValue = (val) => `${val || ""}`.trim()

export const useDefaultCustomSelect = (options = []) => {
  const [stateMap, setStateMap] = useState({})

  // --- Helpers ---
  const getState = useCallback(
    (key) => stateMap[key] || defaultFieldState,
    [stateMap]
  )

  const findOption = useCallback(
    (rawVal) => {
      const target = normalizeValue(rawVal)
      return options.find(opt => normalizeValue(opt) === target) || null
    },
    [options]
  )

  const updateState = useCallback((key, updater) => {
    setStateMap(prev => {
      const current = prev[key] || defaultFieldState
      const next = updater(current)

      if (
        current.value === next.value &&
        current.inputValue === next.inputValue &&
        current.invalid === next.invalid &&
        current.pendingResolution === next.pendingResolution
      ) {
        return prev
      }

      return { ...prev, [key]: next }
    })
  }, [])

  // --- Handlers ---
  const handleChange = useCallback(
    (key, _event, newValue, reason) => {
      void _event

      updateState(key, (current) => {
        const raw =
          typeof newValue === "string"
            ? newValue
            : newValue?.value ?? newValue

        const normalized = normalizeValue(raw)

        if (reason === "clear" || !normalized) {
          return defaultFieldState
        }

        const matched = findOption(normalized)
        const labelText = matched || raw

        if (reason === "selectOption") {
          return {
            value: normalizeValue(labelText),
            inputValue: labelText,
            invalid: false,
            pendingResolution: false,
          }
        }

        return {
          value: normalizeValue(labelText),
          inputValue: labelText,
          invalid: current.invalid,
          pendingResolution: false,
        }
      })
    },
    [findOption, updateState]
  )

  const handleInputChange = useCallback(
    (key, _event, newInput, reason) => {
      if (reason !== "input") return

      updateState(key, () => {
        if (!newInput?.trim()) return defaultFieldState

        return {
          value: normalizeValue(newInput),
          inputValue: newInput,
          invalid: false,
          pendingResolution: false,
        }
      })
    },
    [updateState]
  )

  const handleBlur = useCallback(
    (key) => {
      updateState(key, (current) => {
        const raw = current.inputValue || current.value || ""

        if (!raw.trim()) {
          return defaultFieldState
        }

        if (!options.length) {
          return { ...current, pendingResolution: true, invalid: false }
        }

        const match = findOption(raw)

        return {
          value: normalizeValue(match || raw),
          inputValue: match || raw,
          invalid: false,
          pendingResolution: false,
        }
      })
    },
    [findOption, options, updateState]
  )

  // 🔑 Banco / externo → estado interno (manual, sem loop)
  const syncValue = useCallback(
    (key, externalValue, defaultValue) => {
      const normalizedExternal = normalizeValue(externalValue)
      const normalizedDefault = normalizeValue(defaultValue)

      if (
        externalValue === undefined ||
        externalValue === null ||
        normalizedExternal === "" ||
        normalizedExternal === normalizedDefault
      ) {
        return
      }

      updateState(key, () => {
        const match = findOption(normalizedExternal)

        return {
          value: normalizedExternal,
          inputValue: match || externalValue,
          invalid: false,
          pendingResolution: !options.length,
        }
      })
    },
    [findOption, options, updateState]
  )

  // --- Computed ---
  const computed = useCallback(
    (key) => {
      const current = stateMap[key] || defaultFieldState

      return {
        hasError: Boolean(current.invalid),
        isLoading: Boolean(current.pendingResolution),
        hasValue: Boolean(current.value || current.inputValue),
        value: current.value || "",
        inputValue: current.inputValue || "",
      }
    },
    [stateMap]
  )

  // --- Actions ---
  const actions = useMemo(() => ({
    setInputValue: (key, inputValue) => {
      updateState(key, current => ({
        ...current,
        inputValue: inputValue || "",
      }))
    },
    setSelectedValue: (key, value) => {
      updateState(key, current => ({
        ...current,
        value: normalizeValue(value),
        invalid: false,
      }))
    },
    reset: () => setStateMap({}),
  }), [updateState])

  const handlers = useMemo(() => ({
    onChange: handleChange,
    onInputChange: handleInputChange,
    onBlur: handleBlur,
    syncValue,
  }), [handleChange, handleInputChange, handleBlur, syncValue])

  const meta = useMemo(() => ({
    options,
    resolved: Boolean(options.length),
    freeSolo: true,
    getOptionLabel: (opt) => opt,
    normalizeValue,
    filterOptions: (opts, params) => {
      const input = params?.inputValue?.trim()?.toLowerCase() || ""
      if (!input) return opts
      return opts.filter(opt => opt.toLowerCase().includes(input))
    },
    minCharsToFilter: 0,
    noOptionsText: () => "Nenhuma cota encontrada",
    getOptionDisabled: () => false,
  }), [options])

  return useMemo(() => ({
    state: { byKey: stateMap },
    getState,
    actions,
    handlers,
    meta,
    computed,
    cleanup: actions.reset,
  }), [stateMap, getState, actions, handlers, meta, computed])
}
